import { fmtDay } from '@/lib/dates';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarClock, ChevronRight } from 'lucide-react';


// Explicit opt-in for automatic monthly charging; unticked by default.
export function AutoRenewConsent({ checked, onChange, testid }) {
  return (
    <label className={`mt-5 flex items-start gap-3 p-4 rounded-2xl border cursor-pointer select-none transition-colors ${checked ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-border bg-card hover:border-[#00C8FF]/50'}`} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        className="mt-[3px] w-[22px] h-[22px] md:w-5 md:h-5 md:mt-[4px] flex-shrink-0 accent-[#00C8FF] cursor-pointer" data-testid={`${testid}-checkbox`} />
      <span className="text-base md:text-[17px] leading-relaxed text-foreground/85">
        <strong className="font-semibold text-foreground">Renew automatically:</strong> I authorise YourReGen to charge my card every month until I cancel. Leave unticked to pay for one month and renew yourself.
      </span>
    </label>
  );
}

export function CardOnlyNote({ testid }) {
  return <p className="mt-3 text-[11px] text-muted-foreground text-center" data-testid={testid}>Pay securely by credit or debit card.</p>;
}

export function SubscriptionRow() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/subscription/me').then(r => setSub(r.data)).catch(() => {}); }, []);
  if (!sub?.plan) return null;
  const active = sub.status === 'active';
  const renewPath = (sub.renew_url || '').replace(/^https?:\/\/[^/]+/, '');
  const cancel = async () => {
    setBusy(true);
    try { const r = await api.post('/subscription/auto-renew/cancel'); setSub(r.data); toast.success('Automatic renewal turned off'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="row-subscription" className="w-full px-4 py-3.5 flex items-center gap-3 border-b border-[var(--c-hair)] last:border-0 hover:bg-[var(--c-film)] transition-colors text-left">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#00C8FF]/10"><CalendarClock className="w-4 h-4 text-[#00C8FF]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Subscription</div>
          <div className="text-[11px] text-muted-foreground truncate" data-testid="row-subscription-sub">
            {active ? `${sub.plan_label} · paid through ${fmtDay(sub.paid_through_date)}` : `${sub.plan_label} · ended`}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] w-[calc(100%-2rem)] rounded-2xl grid-cols-1 [&>*]:min-w-0" data-testid="subscription-dialog">
          <DialogHeader><DialogTitle>{sub.plan_label}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm [&>div]:gap-4 [&>div>span:last-child]:text-right">
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span data-testid="subscription-status">{active ? 'Active' : 'Ended'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Plan length</span><span>{sub.term_months} month{sub.term_months > 1 ? 's' : ''}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{active ? 'Paid through' : 'Ended on'}</span><span data-testid="subscription-paid-through">{fmtDay(sub.paid_through_date)}</span></div>
            {active && <div className="flex justify-between"><span className="text-muted-foreground">{sub.auto_renew ? 'Next charge & period' : 'Renewal period would start'}</span><span data-testid="subscription-next-start">{fmtDay(sub.next_start)}</span></div>}
            {active && sub.next_programme_date && <div className="flex justify-between"><span className="text-muted-foreground">Next programme update</span><span data-testid="subscription-next-programme">{fmtDay(sub.next_programme_date)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Automatic renewal</span><span data-testid="subscription-auto-renew">{sub.auto_renew ? 'On — charged monthly' : 'Off'}</span></div>
          </div>
          {active && !sub.auto_renew && <p className="text-[11px] text-muted-foreground">Renewing early adds the new months after your current end date.</p>}
          <div className="flex flex-col gap-2 mt-1">
            {!sub.auto_renew && (
              <Button onClick={() => nav(renewPath)} data-testid="subscription-renew" className="bg-[#00C8FF] text-[#081726] font-semibold whitespace-normal h-auto py-2.5">
                {active ? 'Renew now' : 'Renew'}
              </Button>
            )}
            {sub.auto_renew && (
              <Button variant="outline" disabled={busy} onClick={cancel} data-testid="subscription-cancel-auto-renew" className="border-[var(--c-hair)]">
                Turn off automatic renewal
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
